import { useState } from 'react';
import { AccountModel, DiaChi, DonViCoSo, HanThanhToan, KhachHang, QuyenChungTu, Tax } from '@/types/schemas';

export interface FormFieldState {
  // Customer information
  khachHang: KhachHang | null;
  taiKhoan: AccountModel | null;
  hanThanhToan: HanThanhToan | null;

  // Document information
  quyenChungTu: QuyenChungTu | null;
  donViCoSo: DonViCoSo | null;

  //Other
  coQuanThue: KhachHang | null;
  diaChiGiaoHang: DiaChi | null;

  // Tax information
  taxModel: Tax | null;
  taiKhoanThue: AccountModel | null;

  // Other information
  soChungTu: string;
}

export interface FormFieldActions {
  // Search field setters
  setKhachHang: (khachHang: KhachHang | null) => void;

  setTaiKhoan: (taiKhoan: AccountModel | null) => void;
  setHanThanhToan: (hanThanhToan: HanThanhToan | null) => void;
  setQuyenChungTu: (quyenChungTu: QuyenChungTu) => void;
  setDonViCoSo: (donViCoSo: DonViCoSo) => void;

  setSoChungTu: (soChungTu: string) => void;

  setCoQuanThue: (coQuanThue: KhachHang) => void;
  setDiaChiGiaoHang: (diaChiGiaoHang: DiaChi) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  // Customer information
  khachHang: null,
  taiKhoan: null,
  hanThanhToan: null,

  // Document information
  quyenChungTu: null,
  donViCoSo: null,

  coQuanThue: null,
  diaChiGiaoHang: null,

  // Tax information
  taxModel: null,
  taiKhoanThue: null,

  // Other information
  soChungTu: ''
};

function transformInitialData(initialData?: BanHangRecord): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    // Customer information
    khachHang: initialData.ma_kh_data || null,

    taiKhoan: initialData.tk_data || null,
    hanThanhToan: initialData.ma_tt_data || null,

    coQuanThue: initialData.ma_kh9_data || null,
    diaChiGiaoHang: initialData.ma_dc_data || null,

    // Document information
    quyenChungTu: initialData.so_ct_data || null,
    donViCoSo: initialData.unit_id_data || null,

    // Other information
    soChungTu: initialData.so_ct || ''
  };
}

export function useFormFieldState(initialData?: BanHangRecord): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: FormFieldActions = {
    // Search field setters
    setKhachHang: (khachHang: KhachHang | null) => {
      setState(prev => ({
        ...prev,
        khachHang
      }));
    },

    setTaiKhoan: (taiKhoan: AccountModel | null) => {
      setState(prev => ({
        ...prev,
        taiKhoan
      }));
    },

    setHanThanhToan: (hanThanhToan: HanThanhToan | null) => {
      setState(prev => ({
        ...prev,
        hanThanhToan
      }));
    },

    setQuyenChungTu: (quyenChungTu: QuyenChungTu) => {
      setState(prev => ({
        ...prev,
        quyenChungTu
      }));
    },

    setDonViCoSo: (donViCoSo: DonViCoSo) => {
      setState(prev => ({
        ...prev,
        donViCoSo
      }));
    },

    setSoChungTu: (soChungTu: string) => {
      setState(prev => ({ ...prev, soChungTu }));
    },

    setCoQuanThue: (coQuanThue: KhachHang) => {
      setState(prev => ({
        ...prev,
        coQuanThue
      }));
    },

    setDiaChiGiaoHang: (diaChiGiaoHang: DiaChi) => {
      setState(prev => ({
        ...prev,
        diaChiGiaoHang
      }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
